import { useEffect, useMemo, useRef, useState } from 'react';
import { isAlgoliaConfigured, searchBrands } from '../services/algolia';
import {
  mergeBrandSearchResults,
  type BrandSearchable,
} from '../services/brandSearchQuery';

/**
 * Long enough that a normal typing burst is one request, short enough that the
 * list feels live. Matches the debounce the reference listing settled on.
 */
const DEFAULT_DEBOUNCE_MS = 250;

/**
 * The brands index holds 560 records, so one page covers any query. Only
 * `objectID` is retrieved — the caller already holds the full documents — which
 * keeps a keystroke's response to a few KB.
 */
const DEFAULT_HITS_PER_PAGE = 1000;
const ID_ONLY = ['objectID'] as const;

/**
 * Filter a brand list by a search term using Algolia's typo tolerance, prefix
 * matching, accent folding and parent-org search, falling back to the old
 * substring match when Algolia can't answer.
 *
 * Every admin brand picker had its own `useMemo` running
 * `name.toLowerCase().includes(term)` over a locally loaded list, which is why
 * they all demanded exact spelling. This hook is the one place that behaviour
 * now lives, so the pickers stay in parity.
 *
 * The caller keeps owning the brand objects: Algolia decides which ids match and
 * in what order, and the hook maps those back onto `items`. That matters because
 * the index carries a subset of the fields these surfaces render, and because
 * `onChange` handlers hand back ids the caller's own list has to resolve.
 *
 * `items` should be referentially stable (state or a `useMemo`) — it is a
 * dependency of the returned memo, so a fresh array every render re-derives the
 * result every render.
 */
export function useBrandSearch<T extends BrandSearchable>(
  items: T[],
  term: string,
  opts?: { debounceMs?: number; hitsPerPage?: number },
): { results: T[]; searching: boolean; activeTerm: string } {
  const debounceMs = opts?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const hitsPerPage = opts?.hitsPerPage ?? DEFAULT_HITS_PER_PAGE;

  const [debouncedTerm, setDebouncedTerm] = useState(term);
  /** Ranked ids from Algolia, or null for "no Algolia answer" (see below). */
  const [hitIds, setHitIds] = useState<string[] | null>(null);
  const [searching, setSearching] = useState(false);
  /**
   * Guards against out-of-order responses: a slow request for "lo" must not
   * overwrite the already-arrived result for "lorier".
   */
  const requestSeq = useRef(0);

  useEffect(() => {
    // Clearing the box restores the full list immediately — waiting out the
    // debounce to show MORE rows just looks like lag.
    if (!term.trim()) {
      setDebouncedTerm(term);
      return;
    }
    const timer = setTimeout(() => setDebouncedTerm(term), debounceMs);
    return () => clearTimeout(timer);
  }, [term, debounceMs]);

  useEffect(() => {
    const trimmed = debouncedTerm.trim();
    if (!trimmed || !isAlgoliaConfigured()) {
      setHitIds(null);
      setSearching(false);
      return;
    }
    const seq = ++requestSeq.current;
    setSearching(true);
    searchBrands(trimmed, {}, 0, hitsPerPage, { attributes: ID_ONLY })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setHitIds(res.hits.map((h) => h.objectID));
      })
      .catch((error) => {
        if (seq !== requestSeq.current) return;
        // Non-fatal: null drops the merge back to the local substring filter,
        // which is exactly what these pickers did before Algolia.
        console.warn('Brand search unavailable, using local filter', error);
        setHitIds(null);
      })
      .finally(() => {
        if (seq === requestSeq.current) setSearching(false);
      });
  }, [debouncedTerm, hitsPerPage]);

  const results = useMemo(
    () => mergeBrandSearchResults(items, hitIds, debouncedTerm),
    [items, hitIds, debouncedTerm],
  );

  // `activeTerm` is the term `results` is actually FOR — the debounced one, not
  // the box. The relevance sort keys off it so the listing doesn't flip into
  // ranked order a beat before the ranked ids arrive, which would show one
  // order and then jump to another.
  return { results, searching, activeTerm: debouncedTerm };
}
